import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Factor, AuthenticatorAssuranceLevels } from '@supabase/supabase-js';

interface MfaStatus {
  factors: Factor[];
  currentLevel: AuthenticatorAssuranceLevels | null;
  nextLevel: AuthenticatorAssuranceLevels | null;
  hasVerifiedFactor: boolean;
  isLoading: boolean;
  refresh: () => Promise<void>;
}

export function useMfa(): MfaStatus {
  const [factors, setFactors] = useState<Factor[]>([]);
  const [currentLevel, setCurrentLevel] = useState<AuthenticatorAssuranceLevels | null>(null);
  const [nextLevel, setNextLevel] = useState<AuthenticatorAssuranceLevels | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const [aalResult, factorsResult] = await Promise.all([
        supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
        supabase.auth.mfa.listFactors(),
      ]);

      if (aalResult.data) {
        setCurrentLevel(aalResult.data.currentLevel);
        setNextLevel(aalResult.data.nextLevel);
      }

      if (factorsResult.data) {
        setFactors(factorsResult.data.all);
      }
    } catch (e) {
      console.error('Failed to fetch MFA status:', e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const hasVerifiedFactor = factors.some(
    (f) => f.status === 'verified' && (f.factor_type === 'totp' || f.factor_type === 'phone')
  );

  return { factors, currentLevel, nextLevel, hasVerifiedFactor, isLoading, refresh };
}
